// קומפוננטה נפרדת לאנימציה מתקדמת של AI

import React, { useState, useEffect } from 'react';
const AdvancedAIAnimation = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [particles, setParticles] = useState([]);
  
  const steps = [
    { id: 1, text: 'איסוף נתונים', icon: '📊' },
    { id: 2, text: 'ניתוח ביצועים', icon: '🔍' },
    { id: 3, text: 'השוואת דמי ניהול', icon: '💰' },
    { id: 4, text: 'יצירת המלצה', icon: '🎯' }
  ];

  const messages = [
    'איסוף נתוני תשואות היסטוריים...',
    'ניתוח ביצועים ברוב התקופות...',
    'השוואת עלויות ודמי ניהול...',
    'יצירת המלצה מותאמת אישית...'
  ];

  useEffect(() => {
    // יצירת חלקיקים
    const newParticles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: Math.random() * 2 + 2
    }));
    setParticles(newParticles);

    // אנימציה של השלבים
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, 800);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white bg-opacity-95 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full mx-4 text-center relative overflow-hidden">
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {particles.map(particle => (
            <div
              key={particle.id}
              className="absolute w-1 h-1 bg-purple-600 rounded-full opacity-0 animate-pulse"
              style={{
                left: `${particle.left}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
                animation: `particleFloat ${particle.duration}s ease-in-out infinite ${particle.delay}s`
              }}
            />
          ))}
        </div>

        {/* AI Brain */}
        <div className="relative w-32 h-32 mx-auto mb-8">
          {/* Brain outline */}
          <div className="absolute inset-0 border-4 border-purple-600 rounded-full animate-spin" 
               style={{
                 borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                 animationDuration: '4s'
               }}>
          </div>
          
          {/* Brain waves */}
          <div className="absolute inset-0 flex items-center justify-center">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="absolute border-2 border-blue-500 rounded-full opacity-0"
                style={{
                  width: `${20 + i * 20}px`,
                  height: `${20 + i * 20}px`,
                  animation: `waveExpand 2s ease-out infinite ${(i - 1) * 0.5}s`
                }}
              />
            ))}
          </div>

          {/* Neural network */}
          <div className="absolute inset-0">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-purple-600 rounded-full opacity-0"
                style={{
                  top: `${20 + i * 15}%`,
                  left: `${30 + (i % 2) * 40}%`,
                  animation: `neuronPulse 1.5s ease-in-out infinite ${(i - 1) * 0.3}s`
                }}
              />
            ))}
          </div>

          {/* Connections */}
          <div className="absolute inset-0">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="absolute h-0.5 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0"
                style={{
                  top: `${25 + i * 20}%`,
                  left: `${25 + i * 10}%`,
                  width: `${20 + i * 10}px`,
                  transform: `rotate(${30 + i * 30}deg)`,
                  animation: `connectionFlow 2s ease-in-out infinite ${(i - 1) * 0.2}s`
                }}
              />
            ))}
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-800 mb-4 opacity-0 animate-fadeInUp">
          🤖 ניתוח AI מתקדם
        </h2>

        {/* Progress container */}
        <div className="bg-purple-50 rounded-xl p-6 mb-6 opacity-0 animate-fadeInUp" 
             style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
          
          {/* Progress bar */}
          <div className="w-full h-2 bg-purple-200 rounded-full overflow-hidden mb-4">
            <div 
              className="h-full bg-gradient-to-r from-purple-600 to-blue-600 rounded-full transition-all duration-1000"
              style={{ 
                width: `${((currentStep + 1) / steps.length) * 100}%`,
                animation: 'progressFill 3s ease-in-out infinite'
              }}
            />
          </div>

          {/* Steps */}
          <div className="flex justify-between items-center mb-4">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex flex-col items-center transition-all duration-500 ${
                  index === currentStep ? 'opacity-100 scale-110' : 'opacity-30'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mb-2 ${
                  index === currentStep 
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg' 
                    : 'bg-gray-400'
                }`}>
                  {step.icon}
                </div>
                <span className="text-xs text-gray-600 font-medium">{step.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Status text */}
        <div className="text-purple-600 font-medium opacity-0 animate-fadeInUp" 
             style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
          <div className="flex items-center justify-center mb-2">
            <div className="flex space-x-1 mr-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                  style={{ animationDelay: `${(i - 1) * 0.2}s` }}
                />
              ))}
            </div>
            <span>{messages[currentStep]}</span>
          </div>
        </div>
      </div>

    <style>{`
        @keyframes particleFloat {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0);
          }
          50% {
            opacity: 1;
            transform: translateY(-10px) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-30px) scale(0);
          }
        }

        @keyframes waveExpand {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }

        @keyframes neuronPulse {
          0%, 100% { 
            opacity: 0;
            transform: scale(1);
          }
          50% { 
            opacity: 1;
            transform: scale(1.3);
          }
        }

        @keyframes connectionFlow {
          0% { 
            opacity: 0;
            transform: scaleX(0);
          }
          50% { 
            opacity: 1;
            transform: scaleX(1);
          }
          100% { 
            opacity: 0;
            transform: scaleX(0);
          }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes progressFill {
          0% { width: 0%; }
          25% { width: 30%; }
          50% { width: 60%; }
          75% { width: 85%; }
          100% { width: 100%; }
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AdvancedAIAnimation;